import { Empty, IconFont, MobileModal } from '@byecode/ui'
import { AppUserStatus } from '@lighthouse/core'
import type { DrawerProps } from '@mantine/core'
import { clone, findIndex } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateEffect } from 'react-use'

import { useApplicationContext, useLanguageContext } from '../../../contexts'
import type { UserOption } from '../../../types'
import { getImageFullUrlInApplication } from '../../../utils'
import { UserAvatar } from '../../UserAvatar'
import { DropDownList } from './DropDownList'
import * as SC from './styles'

interface PersonDrawerProps {
    opened: boolean
    isMultiple?: boolean
    leftIcon?: string
    selectOptions: UserOption[]
    value: string[]
    title: string
    target: DrawerProps['target']
    onChange?: (val: string[]) => void
    onClose: () => void
}

export const PersonDrawer: React.FC<PersonDrawerProps> = ({
    opened,
    leftIcon,
    isMultiple = false,
    selectOptions,
    title,
    value,
    target,
    onClose,
    onChange
}) => {
    const [selectValue, setSelectValue] = useState(value)
    const [searchVal, setSearchVal] = useState('')

    const { t } = useTranslation()
    const { appId } = useApplicationContext()

    const { convertTextByLanguage } = useLanguageContext()
    useUpdateEffect(() => {
        if (value !== selectValue) {
            setSelectValue(value)
        }
    }, [value])

    const handleSelectVal = useCallback(
        (val: string) => {
            const cloneSelectValue = clone(selectValue)
            const index = findIndex(id => id === val, cloneSelectValue)
            if (index >= 0) {
                cloneSelectValue.splice(index, 1)
                return cloneSelectValue
            }
            if (isMultiple) {
                cloneSelectValue.push(val)
                return cloneSelectValue
            }
            return [val]
        },
        [isMultiple, selectValue]
    )

    const handleFinish = useCallback(() => {
        onChange?.(selectValue)
        onClose()
    }, [onChange, onClose, selectValue])

    const handleSelectChange = useCallback(
        (val: string) => {
            const newValue = handleSelectVal(val)
            setSelectValue(newValue)
            if (!isMultiple) {
                onChange?.([val])
                onClose()
            }
        },
        [handleSelectVal, isMultiple, onChange, onClose]
    )

    const list = useMemo(
        () =>
            selectOptions.filter(
                item =>
                    (item.id.toLocaleLowerCase().includes(searchVal.toLocaleLowerCase()) ||
                        item.name.toLocaleLowerCase().includes(searchVal.toLocaleLowerCase())) &&
                    item.status !== AppUserStatus.DEPART
            ),
        [searchVal, selectOptions]
    )

    return (
        <MobileModal
            leftSlot={!!leftIcon && <IconFont type={leftIcon} />}
            withCloseIcon={!isMultiple}
            data-ignore-click-away
            target={target}
            title={title}
            open={opened}
            styles={{
                modal: {
                    height: '50%'
                }
            }}
            onClose={onClose}
            extra={
                isMultiple ? (
                    <SC.FinishButton role="button" onClick={handleFinish}>
                        {convertTextByLanguage('complete')}
                    </SC.FinishButton>
                ) : null
            }
        >
            <SC.Container>
                <SC.SearchInput
                    value={searchVal}
                    placeholder={t('personNameOrPersonId')}
                    onChange={e => setSearchVal(e.currentTarget.value)}
                    onClear={() => setSearchVal('')}
                    size="md"
                    clearable
                    prefix={<IconFont type="SearchLine" />}
                    styles={{
                        wrapper: {
                            width: '100%'
                        }
                    }}
                />
                {list.length > 0 ? (
                    <DropDownList options={list} value={selectValue} appId={appId} onChange={handleSelectChange} />
                ) : (
                    <Empty
                        styles={{
                            root: {
                                padding: '42px'
                            }
                        }}
                        icon="SearchLine"
                        description={t('noFindPerson')}
                    />
                )}
            </SC.Container>
        </MobileModal>
    )
}
